import axios from 'axios';

class AuthService {
  constructor() {
    this.axios = axios.create({
      baseURL: '/api',
      withCredentials: true, // This is crucial for sending and receiving HTTP-only cookies
    });
    this.setupAxiosDefaults();
  }

  setupAxiosDefaults() {
    // Set default timeout
    this.axios.defaults.timeout = 10000; // 10 seconds

    // Set default headers
    this.axios.defaults.headers.common['Content-Type'] = 'application/json';

    // Request Interceptor: No proactive token refresh logic here.
    // HTTP-only tokens cannot be read by client-side JavaScript to check expiry or manually add to Authorization headers.
    // The browser automatically handles sending HTTP-only cookies with requests.
    this.axios.interceptors.request.use(
      (config) => {
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response Interceptor: Handles 401 errors
    this.axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const errorMessage = error.response?.data?.message;

        // DEBUG: Log the exact error message received from the backend
        console.log(
          '[AuthService Interceptor] Received error message:',
          errorMessage
        );
        console.log(
          '[AuthService Interceptor] Request URL:',
          originalRequest.url
        );
        console.log(
          '[AuthService Interceptor] HTTP Status:',
          error.response?.status
        );

        // Handle network errors (no response from server)
        if (!error.response) {
          return Promise.reject({
            message: 'Network error. Please check your connection.',
            original: error,
          });
        }

        // Handle timeouts (request took too long)
        if (error.code === 'ECONNABORTED') {
          return Promise.reject({
            message: 'Request timed out. Please try again.',
            original: error,
          });
        }

        // Handle 401 Unauthorized errors
        if (error.response.status === 401) {
          // If the 401 is due to the *absence* of a session token
          if (
            errorMessage?.includes('Not authenticated') &&
            originalRequest.url.includes('/auth/verify')
          ) {
            console.log(
              '[AuthService Interceptor] Resolving as NO_SESSION_TOKEN_PRESENT'
            );
            return Promise.resolve({
              success: false,
              type: 'NO_SESSION_TOKEN_PRESENT',
            });
          }

          // Skip session refresh for auth endpoints that don't require an existing session
          const noSessionEndpoints = [
            '/auth/verify-code',
            '/auth/verify-email',
            '/auth/update-email',
            '/auth/verify-email-change',
            '/auth/confirm-email-change',
          ];

          if (
            noSessionEndpoints.some((endpoint) =>
              originalRequest.url.includes(endpoint)
            )
          ) {
            console.log(
              `[AuthService Interceptor] Auth endpoint ${originalRequest.url} failed, skipping session refresh`
            );
            return Promise.reject({
              message: errorMessage || 'Authentication failed',
              original: error,
            });
          }

          // For any other 401 errors, reject the promise
          this.clearAuthState();
          return Promise.reject({
            message: 'Session expired. Please log in again.',
            original: error,
          });
        }

        // For any other errors, reject the promise
        return Promise.reject({
          message: errorMessage || 'An error occurred',
          original: error,
        });
      }
    );
  }

  // --- Magic Code Authentication Methods ---

  async requestCode(email) {
    const response = await this.axios.post('/auth/request-code', {
      email,
    });
    return response.data;
  }

  async verifyCode(email, code) {
    const response = await this.axios.post('/auth/verify-code', {
      email,
      code,
    });
    return response.data;
  }

  async verifyEmail(token) {
    const response = await this.axios.get(`/auth/verify-email?token=${token}`);
    return response.data;
  }

  async updateEmail(newEmail) {
    const response = await this.axios.post('/auth/update-email', {
      newEmail,
    });
    return response.data;
  }

  async verifyEmailChange(token) {
    const response = await this.axios.get(
      `/auth/confirm-email-change?token=${token}`
    );
    return response.data;
  }

  async verifyEmailChangeCode(code) {
    const response = await this.axios.post('/auth/verify-email-change', {
      code,
    });
    return response.data;
  }

  // --- Session Management Methods ---

  async verifyAuth() {
    const response = await this.axios.get('/auth/verify');
    return response.data;
  }

  async logout() {
    const response = await this.axios.post('/auth/logout');
    return response.data;
  }

  // --- Address Management Methods ---

  async getAddressData() {
    const response = await this.axios.get('/auth/address');
    return response.data;
  }

  async updateAddress(addressData) {
    const response = await this.axios.post('/auth/address', addressData);
    return response.data;
  }

  async deleteAccount() {
    const response = await this.axios.delete('/auth/account');
    return response.data;
  }

  // --- Utility Method ---
  clearAuthState() {
    // This method should be implemented to clear any client-side state
    // (e.g., React context state, user data in memory) that indicates
    // the user is authenticated. The actual session cookies are cleared by the backend.
    console.log('Client-side authentication state cleared.');

    // Dispatch a custom event to notify AuthContext to clear its state
    const sessionExpiredEvent = new CustomEvent('sessionExpired', {
      detail: {
        message: 'Session expired. Please log in again.',
        timestamp: new Date().toISOString(),
      },
    });
    window.dispatchEvent(sessionExpiredEvent);
  }
}

export const authService = new AuthService();
